/**
 * Hook para buscar atividades PENDENTES (não concluídas) de um contato
 * Busca atividades de negócios E de clientes vinculados ao contato
 * 
 * Lógica de busca:
 * 1. Buscar customer_id vinculado ao contato (via crm_customers.main_contact_id)
 * 2. Buscar deal_ids vinculados ao contato (via crm_deals.contact_id)
 * 3. Buscar atividades onde:
 *    - completed_at IS NULL (pendente)
 *    - (customer_id = customer OR deal_id IN deal_ids)
 * 
 * Segurança: Respeita visibilidade do vendedor (só vê deals que tem acesso)
 * 
 * RELEASE: 2026-01-14
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

// Namespace para logs deste módulo
const LOG_NS = 'useContactPendingActivities';

/**
 * Tipos de atividade do CRM
 */
export type ActivityType = 'note' | 'task' | 'call' | 'email' | 'meeting' | 'phase_change' | 'status_change' | 'value_change' | 'assignment_change' | 'creation' | 'attachment';

/**
 * Interface para atividade pendente do contato
 */
export interface ContactPendingActivity {
  id: string;
  activity_type: ActivityType;
  title: string;
  description: string | null;
  scheduled_at: string | null;
  is_system: boolean;
  deal_id: string | null;
  customer_id: string | null;
  created_at: string;
  // Dados do negócio (se vinculado)
  deal: {
    id: string;
    title: string;
  } | null;
  // Dados do performer
  performer: {
    name: string;
    avatar_url: string | null;
  } | null;
}

/**
 * Opções do hook
 */
interface UseContactPendingActivitiesOptions {
  /** Habilitar lazy loading - só busca quando true */
  enabled?: boolean;
  /** Limite de atividades a buscar */
  limit?: number;
}

/**
 * Hook para buscar atividades pendentes de um contato
 * 
 * @param contactId - ID do contato
 * @param options - Opções de configuração
 * @returns Lista de atividades pendentes, loading state e mutations
 */
export function useContactPendingActivities(
  contactId: string | null,
  options: UseContactPendingActivitiesOptions = {}
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const { enabled = true, limit = 20 } = options;

  // Query principal — usa RPC server-side `get_contact_pending_activities`,
  // que aplica validação de tenant, permissão de fase e filtros de visibilidade.
  const activitiesQuery = useQuery({
    queryKey: ['contact-pending-activities', contactId, currentTenant?.id, limit],
    queryFn: async (): Promise<ContactPendingActivity[]> => {
      if (!contactId || !currentTenant?.id) {
        return [];
      }

      logger.debug(LOG_NS, 'Buscando atividades pendentes via RPC', { contactId });

      const { data, error } = await supabase.rpc(
        'get_contact_pending_activities' as never,
        {
          p_contact_id: contactId,
          p_limit: limit,
          p_tenant_hint: currentTenant.id,
        } as never,
      );

      if (error) {
        logger.error(LOG_NS, 'Erro ao buscar atividades pendentes', { error });
        throw error;
      }

      // Mapeia colunas planas da RPC para o shape esperado pela UI
      // v1.1.0 — Notas não participam de pendências (defesa em profundidade)
      return ((data ?? []) as Array<Record<string, unknown>>)
        .filter((row) => row.activity_type !== 'note')
        .map((row): ContactPendingActivity => ({
        id: row.id as string,
        activity_type: row.activity_type as ActivityType,
        title: row.title as string,
        description: (row.description as string | null) ?? null,
        scheduled_at: (row.scheduled_at as string | null) ?? null,
        is_system: Boolean(row.is_system),
        deal_id: (row.deal_id as string | null) ?? null,
        customer_id: (row.customer_id as string | null) ?? null,
        created_at: row.created_at as string,
        deal: row.deal_id
          ? { id: row.deal_id as string, title: (row.deal_title as string) ?? '' }
          : null,
        performer: row.performer_name
          ? {
              name: (row.performer_name as string) || 'Usuário',
              avatar_url: (row.performer_avatar as string | null) ?? null,
            }
          : null,
      }));
    },
    enabled: enabled && !!contactId && !!currentTenant?.id,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  // Mutation — usa RPC `complete_deal_activity` para validação server-side
  const completeActivity = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase.rpc('complete_deal_activity' as never, {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-pending-activities', contactId] });
      toast.success('Atividade concluída!');
    },
    onError: (error) => {
      logger.error(LOG_NS, 'Erro ao concluir atividade', { error });
      toast.error('Erro ao concluir atividade');
    },
  });

  return {
    activities: activitiesQuery.data || [],
    isLoading: activitiesQuery.isLoading,
    error: activitiesQuery.error,
    refetch: activitiesQuery.refetch,
    completeActivity,
  };
}

/**
 * Configuração visual por tipo de atividade
 */
export function getActivityTypeConfig(type: ActivityType) {
  const configs: Record<ActivityType, { icon: string; colorClass: string; label: string }> = {
    note: { icon: 'FileText', colorClass: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400', label: 'Nota' },
    task: { icon: 'CheckSquare', colorClass: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400', label: 'Lembrete' },
    call: { icon: 'Phone', colorClass: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400', label: 'Ligação' },
    email: { icon: 'Mail', colorClass: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400', label: 'Email' },
    meeting: { icon: 'Users', colorClass: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400', label: 'Reunião' },
    phase_change: { icon: 'ArrowRight', colorClass: 'bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-400', label: 'Mudança de Fase' },
    status_change: { icon: 'RefreshCw', colorClass: 'bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-400', label: 'Mudança de Status' },
    value_change: { icon: 'DollarSign', colorClass: 'bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-400', label: 'Mudança de Valor' },
    assignment_change: { icon: 'UserCheck', colorClass: 'bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-400', label: 'Reatribuição' },
    creation: { icon: 'Plus', colorClass: 'bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-400', label: 'Criação' },
    attachment: { icon: 'Paperclip', colorClass: 'bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-400', label: 'Anexo' }
  };

  return configs[type] || configs.note;
}
